import Logo from '@/assets/logo.jpg';
import { UserAvatar } from '@/lib/components/core/auth/userAvatar';
import { Roles, tokenContainsRole, useUserContext } from '@/lib/context/userContext';
import { BookOutlined, DownOutlined, InfoCircleOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Select, Space, Spin } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Feature } from '../features/feature';
import styles from './core.module.scss';
import { useNavigate } from 'react-router-dom';
import { useGetDeelnemers, useGetPublicHandleidingen, useGetVisibleHandleidingen } from '@/lib/queries/content';
import { useFeature } from '@/lib/hooks/useFeature';
import { DeelnemerOutput } from '@/lib/types/deelnemer';
import { PaginationQueryParameters } from '@/lib/types';
import { downloadPdfFromUrl } from '@/lib/utils/pdf';
import { PdfViewerModal } from '../pdf-viewer';
const { Header } = Layout;

interface MainHeaderProps {
  onLogin: () => void;
  onLogout: () => void;
  isAuthenticated: boolean;
}

interface OnBehalfProps {
  deelnemerId: string;
  deelnemerBedrijfsnaam: string;
}

export const MainHeader: React.FunctionComponent<MainHeaderProps> = ({ onLogin, onLogout, isAuthenticated }) => {
  const { t } = useTranslation('core');
  const { username } = useUserContext();
  const navigate = useNavigate();
  const showEditButton = tokenContainsRole([Roles.Administrator]);
  const showOnBehalfFeature = useFeature('show-on-behalf');
  const showOnBehalf = tokenContainsRole([Roles.Administrator]) && showOnBehalfFeature;
  const showGuideManagementButton = useFeature('show-handleiding-button');

  const [onBehalfFilterState, setOnBehalfFilterState] = useState<PaginationQueryParameters<DeelnemerOutput>>({
    page: 1,
    pageSize: 999,
  });
  const [selectedOnBehalf, setSelectedOnBehalf] = useState<OnBehalfProps>({
    deelnemerId: localStorage.getItem('selectedDeelnemerId') ?? '',
    deelnemerBedrijfsnaam: localStorage.getItem('selectedDeelnemerBedrijfsnaam') ?? '',
  });
  const [selectedGuide, setSelectedGuide] = useState<{ id: number; name: string } | null>(null);

  const { data: publicPdfList } = useGetPublicHandleidingen();
  const { data: visiblePdfList } = useGetVisibleHandleidingen(isAuthenticated);
  const { data: onBehalfDeelnemerData, isLoading: onBehalfDeelnemerDataIsLoading } = useGetDeelnemers(onBehalfFilterState);

  const onPrivacy = () => {
    navigate('/privacy');
  };

  let publicGuideMenuItems;
  if (!publicPdfList) {
    publicGuideMenuItems = [
      {
        key: 'loading',
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spin size="small" /> {t('userMenu.loading')}
          </div>
        ),
        disabled: true,
      },
    ];
  } else if (publicPdfList.length === 0) {
    publicGuideMenuItems = [{ key: 'empty', label: t('userMenu.noGuides'), disabled: true }];
  } else {
    publicGuideMenuItems = publicPdfList.map(pdf => ({
      key: `public-guide-${pdf.id}`,
      label: pdf.name,
      onClick: () => downloadPdfFromUrl(`${import.meta.env.VITE_CONFIG_API_URL}/handleiding/${pdf.id}/download`, pdf.name),
    }));
  }

  const roleGuideMenuItems =
    visiblePdfList
      ?.filter(pdf => !pdf.isPubliek)
      .map(pdf => ({
        key: `role-guide-${pdf.id}`,
        label: pdf.name,
        onClick: () => setSelectedGuide({ id: pdf.id, name: pdf.name }),
      })) ?? [];

  const guideMenuItems = [
    ...(showEditButton && showGuideManagementButton
      ? [
          {
            key: 'extra-button',
            label: t('userMenu.guide-management'),
            icon: <SettingOutlined />,
            onClick: () => {
              navigate('/content-management?tab=guide');
            },
          },
          {
            key: 'divider',
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'public-documents',
      type: 'group' as const,
      label: t('userMenu.publicDocuments'),
      children: publicGuideMenuItems,
    },
    ...(isAuthenticated && roleGuideMenuItems.length
      ? [
          {
            key: 'personal-documents',
            type: 'group' as const,
            label: t('userMenu.personalDocuments'),
            children: roleGuideMenuItems,
          },
        ]
      : []),
  ];

  const menuItems = [
    {
      key: 1,
      label: t('userMenu.privacy'),
      icon: <InfoCircleOutlined />,
      onClick: () => navigate('/privacy'),
    },
    {
      key: 2,
      label: t('userMenu.logout'),
      icon: <UserOutlined />,
      onClick: onLogout,
    },
  ];

  const handleSearch = (value: string) => {
    setOnBehalfFilterState(() => ({
      filterValues: {
        bedrijfsnaam: value ? [value] : undefined,
      },
      page: 1,
      pageSize: 9990,
    }));
  };

  const handleSelect = (option: any) => {
    const { value, label } = option;
    localStorage.setItem('selectedDeelnemerId', value);
    localStorage.setItem('selectedDeelnemerBedrijfsnaam', label);
    setSelectedOnBehalf({ deelnemerId: value, deelnemerBedrijfsnaam: label });
  };

  const handleClear = () => {
    localStorage.removeItem('selectedDeelnemerId');
    localStorage.removeItem('selectedDeelnemerBedrijfsnaam');
    setSelectedOnBehalf({ deelnemerId: '', deelnemerBedrijfsnaam: '' });
  };

  return (
    <Header className={styles.header}>
      <div className={styles.logo}>
        <img src={Logo} alt="Logo" />
      </div>
      {isAuthenticated ? (
        <Space>
          {showOnBehalf && (
            <>
              Handelen namens:
              <Select
                showSearch
                allowClear
                style={{ width: 250 }}
                placeholder="Teler selecteren"
                loading={onBehalfDeelnemerDataIsLoading}
                onSearch={handleSearch}
                onSelect={handleSelect}
                labelInValue
                value={selectedOnBehalf.deelnemerId ? { value: selectedOnBehalf.deelnemerId, label: selectedOnBehalf.deelnemerBedrijfsnaam } : undefined}
                onClear={handleClear}
                filterOption={false}>
                {(onBehalfDeelnemerData?.data ?? ([] as DeelnemerOutput[])).map(item => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.bedrijfsnaam}
                  </Select.Option>
                ))}
              </Select>
            </>
          )}
          <Dropdown menu={{ items: guideMenuItems }} trigger={['click']}>
            <Button>
              <BookOutlined /> {t('userMenu.guides')} <DownOutlined />
            </Button>
          </Dropdown>
          <UserAvatar user={username} items={menuItems} />
        </Space>
      ) : (
        <div>
          <Button type="default" style={{ marginRight: 10 }} onClick={onPrivacy}>
            {t('userMenu.privacy')}
          </Button>
          <Dropdown menu={{ items: guideMenuItems }} trigger={['click']}>
            <Button style={{ marginRight: 10 }}>
              {t('userMenu.guides')} <DownOutlined />
            </Button>
          </Dropdown>
          <Feature flag="show-maintenance-mode">
            <Button type="primary" onClick={onLogin}>
              {t('userMenu.login')}
            </Button>
          </Feature>
        </div>
      )}
      <PdfViewerModal
        open={!!selectedGuide}
        onClose={() => setSelectedGuide(null)}
        pdfId={selectedGuide?.id}
        pdfName={selectedGuide?.name}
        title={selectedGuide?.name ?? ''}
      />
    </Header>
  );
};
